import { CalendarOutlined, EditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Segmented, Spin, Table, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthProvider';
import { updatePropsMyHistoryMentenancePlannigs } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setMyHistoryMentenancePlannings } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../../services/mentenancePlanning/MentenancePlanningService';
import { RootState } from '../../../../../store';
import '../../MentenancePlanningCalendarTab/MentenancePlanningCalendarTab.css';
import MentenancePlanningModal from '../../MentenancePlanningModal/MentenancePlanningModal';

const HistoryMentenancePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();

  const { propsMyHistoryMentenancePlannigs: planningProps } = useSelector(
    (state: RootState) => state.models.mentenancePlanning,
  );
  const mentenancePlannings = useSelector(
    (state: RootState) => state.store.mentenancePlanning.myHistoryMentenancePlanningData ?? [],
  );

  const { searchTerm, skip, take, refresh } = planningProps;
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  const currentPage = useMemo(() => Math.floor(skip / take) + 1, [skip, take]);
  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [calendarFocusDate, setCalendarFocusDate] = useState<Dayjs>(dayjs());

  const pageSizes = [10, 20, 30, 50];

  const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
    1: { label: 'Netransmis', color: '#8c8c8c' },
    2: { label: 'Transmis', color: '#1677ff' },
    3: { label: 'A ajuns', color: '#52c41a' },
    4: { label: 'Finalizata', color: '#13c2c2' },
    5: { label: 'Cerere reprogramare', color: '#faad14' },
  };
  const getStatusDisplay = (statusId?: number, fallbackName?: string) => {
    if (statusId && STATUS_CONFIG[statusId]) return STATUS_CONFIG[statusId];
    return { label: fallbackName || 'Status necunoscut', color: '#722ed1' };
  };
  const getClientName = (record: any) =>
    record?.metadata?.company?.name ?? record?.company?.name ?? 'Client necunoscut';
  const getWorkPlaceName = (record: any) =>
    record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Punct necunoscut';

  /** Helper function to dispatch multiple field updates */
  const updatePlanningProps = useCallback(
    (updates: Record<string, any>) => {
      Object.entries(updates).forEach(([field, value]) =>
        dispatch(updatePropsMyHistoryMentenancePlannigs({ field, value })),
      );
    },
    [dispatch],
  );

  /** Sync state with URL params */
  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    updatePlanningProps({ searchTerm: termInUrl, skip: calculatedSkip, take: pageSizeInUrl });
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };

    setIsSearchTermInitialized(true);
  }, [location.search, take, updatePlanningProps]);

  /** Update URL when search term or pagination changes */
  useEffect(() => {
    if (!isSearchTermInitialized) return;

    const { term, page, pageSize } = previousParamsRef.current;
    if (searchTerm !== term || currentPage.toString() !== page || take.toString() !== pageSize) {
      const params = {
        term: searchTerm || undefined,
        page: currentPage > 1 ? currentPage.toString() : undefined,
        pageSize: take !== 10 ? take.toString() : undefined,
      };

      navigate(`${location.pathname}?${queryString.stringify(params)}`, { replace: true });
      previousParamsRef.current = { term: searchTerm || '', page: currentPage.toString(), pageSize: take.toString() };
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname]);

  /** Fetch maintenance planning data */
  const { isFetching: isLoading } = useQuery({
    queryKey: ['mentenancePlannings', searchTerm, skip, take, refresh],
    queryFn: async () => {
      const data = await MentenancePlanningService.searchMyHistoryMentenancePlanning({
        assignedTechnicianId: profile?.id,
        isMyHistoryMentenancePlannings: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      });

      updatePlanningProps({ totalItems: data?.totalItems || 0, refresh: false });
      dispatch(setMyHistoryMentenancePlannings(data?.response));
      return data?.response;
    },
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: calendarMonthData = [] } = useQuery({
    queryKey: ['mentenancePlanningsHistoryCalendar', profile?.id, viewMode, calendarFocusDate.format('YYYY-MM')],
    queryFn: () =>
      viewMode === 'calendar'
        ? MentenancePlanningService.searchMyHistoryMentenancePlanning({
            assignedTechnicianId: profile?.id,
            isMyHistoryMentenancePlannings: true,
            isStatusIncluded: true,
            isCompanyIncluded: true,
            isContractIncluded: true,
            isWorkPlaceIncluded: true,
            isAssignedTechnicianIncluded: true,
            isMentenancePlanningSystemTypeIncluded: true,
            isInterventionFileIncluded: true,
            month: calendarFocusDate.month() + 1,
            year: calendarFocusDate.year(),
            take: 500,
            skip: 0,
          }).then((res) => res?.response ?? [])
        : Promise.resolve([]),
    enabled: !!profile?.id && viewMode === 'calendar',
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });

  const calendarEvents = useMemo(() => {
    if (viewMode !== 'calendar') return [];
    return (calendarMonthData as any[])
      .map((record: any) => {
        const eventDate = record.appointment || record.dateEnd;
        if (!eventDate || !dayjs(eventDate).isValid()) return null;
        const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
        return {
          id: record.id,
          title: `#${record.number || '-'} ${getClientName(record)}`,
          start: dayjs(eventDate).format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: statusDisplay.color,
          borderColor: statusDisplay.color,
          textColor: '#ffffff',
          extendedProps: { record, statusLabel: statusDisplay.label },
        };
      })
      .filter(Boolean);
  }, [viewMode, calendarMonthData]);

  /** Debounced search handler */
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      updatePlanningProps({ searchTerm: value, skip: 0 });
    }, 500),
    [updatePlanningProps],
  );

  /** Cleanup debounce */
  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearch = (value: string) => debouncedSearch(value);
  const handleViewInfo = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleCalendarEventClick = (arg: EventClickArg) => {
    const record = arg.event.extendedProps?.record;
    if (record) handleViewInfo(record);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    setCalendarFocusDate(dayjs(arg.view.currentStart));
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const record = eventContent.event.extendedProps?.record as any;
    if (!record) return null;
    const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
    return (
      <div className='maintenance-calendar-event-content'>
        <div className='maintenance-calendar-event-header'>
          <span className='maintenance-calendar-event-number'>#{record.number ?? '-'}</span>
          <Tag color={statusDisplay.color} className='maintenance-calendar-event-status'>
            {statusDisplay.label}
          </Tag>
        </div>
        <div className='maintenance-calendar-event-text maintenance-calendar-event-client'>{getClientName(record)}</div>
        <div className='maintenance-calendar-event-text'>{getWorkPlaceName(record)}</div>
      </div>
    );
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const baseColumns: any = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color='blue'>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      render: (types: any[]) => types?.map((t: any) => `[${t.systemType.name}]`).join(' ') || '-',
    },
    {
      title: 'Perioada programata',
      dataIndex: 'appointment',
      render: (value: any) => moment(value).format('DD-MM-YYYY') || '-',
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    { title: 'Status', dataIndex: ['status', 'name'] },
    {
      title: 'Actiuni',
      render: (_: any, record: any) => <Button onClick={() => handleViewInfo(record)} icon={<EditOutlined />} />,
      align: 'center',
    },
  ];

  const paginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize: take,
      total: planningProps.totalItems,
      showSizeChanger: true,
      pageSizeOptions: pageSizes.map(String),
      onChange: (page: any, size: any) => updatePlanningProps({ skip: (page - 1) * size, take: size }),
      onShowSizeChange: (_current: any, size: any) => updatePlanningProps({ skip: 0, take: size }),
    }),
    [currentPage, take, planningProps.totalItems, pageSizes, updatePlanningProps],
  );

  if (isLoading) return <Spin className='spinner' size='large' />;

  return (
    <div className='table-page-no-h-scroll'>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align='middle'>
        <Col xs={24} sm={12}>
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode((v as 'table' | 'calendar') ?? 'table')}
            options={[
              { label: 'Tabel', value: 'table', icon: <UnorderedListOutlined /> },
              { label: 'Calendar', value: 'calendar', icon: <CalendarOutlined /> },
            ]}
            style={{ width: '100%', maxWidth: 320 }}
            block
          />
        </Col>
        {viewMode === 'table' && (
          <Col xs={24} sm={12}>
            <Input.Search
              placeholder='Search...'
              defaultValue={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: '100%', maxWidth: 320 }}
              autoFocus
            />
          </Col>
        )}
      </Row>

      {viewMode === 'table' && (
        <div className='table-scroll-wrapper'>
          <Table
            columns={baseColumns}
            dataSource={mentenancePlannings}
            pagination={paginationConfig}
            rowKey='number'
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className='maintenance-calendar-wrapper' style={{ minHeight: 360 }}>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView='dayGridMonth'
            initialDate={calendarFocusDate.format('YYYY-MM-DD')}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
            locale='ro'
            firstDay={1}
            dayMaxEventRows={2}
            events={calendarEvents}
            eventClick={handleCalendarEventClick}
            eventContent={renderEventContent}
            datesSet={handleDatesSet}
            height='auto'
          />
        </div>
      )}

      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
      />
    </div>
  );
};

export default HistoryMentenancePlanning;
